import { animate, motion } from "motion/react";
import { bool, node, object } from "prop-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components/macro";

import useResizeObserver2 from "Utilities/Hooks/useResizeObserver2";

const Mover = styled(motion.div)`
  position: relative; // Containing block safeguard: prevents transform cleanup from shifting abs-positioned children
  ${({ $clip }) => ($clip ? "overflow: clip;" : "")}
  ${({ $shrinkToFit }) => ($shrinkToFit ? "width: fit-content;" : "")}
`;

const Static = styled("div")`
  ${({ $inline }) => ($inline ? "display: inline-flex;" : "")}
`;

/** A div that animates its properties when responding to changes in the content. */
const FnrMovingDiv = forwardRef(
  /**
   * The div itself is not visible. Put the wrapper of your choice around it.
   * @param {object} param0
   * @param {boolean} param0.heightMotion - responds to height changes with animation when true
   * @param {boolean} param0.widthMotion - responds to width changes with animation when true
   * @param {boolean} param0.clip - prevents children from leaving container.
   * @param {boolean} param0.spring - adds the spring variant to framer motion transition.
   * @param {object} param0.transitionOverride - replaces the default size transition spec when set. Used by callers that need this div's size animation to match another animation exactly (same duration + easing). Honors `muted` by forcing duration:0.
   * @param {boolean} param0.shrinkOnly - with `heightMotion`, rests at auto and eases the height on the way DOWN only. For a box whose contents are already at their final layout the moment they grow.
   */
  (
    {
      children,
      heightMotion,
      widthMotion,
      clip = true,
      spring,
      muted,
      transitionOverride,
      shrinkOnly,
      ...rest
    },
    ref
  ) => {
    const [height, setHeight] = useState(0);
    const [width, setWidth] = useState(0);
    const [initialized, setInitialized] = useState(false);
    const contentsRef = useRef();
    const moverRef = useRef(null);
    const [observed, setObserved] = useState(false);

    const lastHeightRef = useRef(null);
    const shrinkRef = useRef(null);
    const shrinking = Boolean(shrinkOnly && heightMotion);

    const duration = useMemo(() => {
      if (muted) return 0;
      if (spring) return undefined;
      else return 0.3;
    }, [muted, spring]);

    const transition = useMemo(() => {
      if (!initialized) return { duration: 0, type: "tween" };
      if (transitionOverride) {
        return muted
          ? { ...transitionOverride, duration: 0 }
          : transitionOverride;
      }
      return {
        ease: "anticipate",
        duration,
        type: spring ? "spring" : "tween",
        damping: 13,
        mass: 0.65,
        stiffness: 160,
      };
    }, [initialized, duration, spring, muted, transitionOverride]);

    const releasePin = () => {
      shrinkRef.current = null;
      if (moverRef.current) moverRef.current.style.height = "";
    };

    /* shrinkOnly's whole rule. A box can do three things with contents taller
       than itself -- clip them, let them spill out of whatever surface it is
       drawing, or hold them back until it has grown. A caller that measures its
       own layout in the commit that renders it can't have the third, so there
       is no correct way to ease a GROW: rest at auto and let it happen in the
       commit's own layout pass. Down is the direction that stays honest, since
       the box is taller than its contents the whole way and the clip cuts
       nothing.

       The pin is written straight to the node rather than through state,
       because the observer fires in the frame's rendering steps -- after layout
       and BEFORE paint. A write from here lands in the frame that is about to
       paint; a setState lands in the next one, and that next one is the frame
       Betty would see the box already collapsed. */
    const pinShrink = (next) => {
      const last = lastHeightRef.current;
      lastHeightRef.current = next;
      if (last === next) return;
      shrinkRef.current?.stop();
      const el = moverRef.current;
      if (last === null || next > last || muted || !el) return releasePin();
      el.style.height = `${last}px`;
      shrinkRef.current = animate(last, next, {
        ...transition,
        onUpdate: (at) => {
          if (moverRef.current) moverRef.current.style.height = `${at}px`;
        },
        onComplete: releasePin,
      });
    };

    useResizeObserver2(contentsRef, () => {
      if (!observed) setObserved(true);
      if (heightMotion && contentsRef.current?.clientHeight !== undefined) {
        if (shrinking) pinShrink(contentsRef.current.clientHeight);
        else setHeight(contentsRef.current.clientHeight);
      }
      if (widthMotion && contentsRef.current?.clientWidth !== undefined) {
        setWidth(contentsRef.current.clientWidth);
      }
    });

    useEffect(() => () => shrinkRef.current?.stop(), []);

    // Enable animated transitions only after the first measurement has painted.
    // useEffect is guaranteed to fire after the browser paints, so this state
    // update can never be batched with the ResizeObserver's width/height update.
    // Uses `observed` (set on first ResizeObserver callback) instead of checking
    // for non-zero dimensions, so components starting at height 0 still initialize.
    useEffect(() => {
      if (!initialized && observed) {
        setInitialized(true);
      }
    }, [initialized, observed]);

    const animateTo = useMemo(() => {
      let localAnimate = {};
      // Until the observer reports, there is no size to animate to, and asserting 0
      // makes the first frame an empty box. Pre-observation the div renders at its
      // natural size (fit-content on Mover covers width). Matters wherever something
      // photographs it -- a cloneNode animation keeps whatever frame it caught.
      // shrinkOnly never asserts one at all; pinShrink owns the height there.
      if (heightMotion && observed && !shrinking) {
        localAnimate.height = height;
      }
      if (widthMotion && observed) {
        localAnimate.width = width;
      }
      return localAnimate;
    }, [height, heightMotion, width, widthMotion, observed, shrinking]);

    // Stable identity, or React detaches and re-attaches every caller's ref on
    // every render. The merge is only here so pinShrink has the node to write.
    const setMoverRef = useCallback(
      (el) => {
        moverRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      },
      [ref]
    );

    return (
      <Mover
        animate={animateTo}
        transition={transition}
        data-cy="FnrMovingDiv"
        $clip={clip}
        $shrinkToFit={widthMotion && !observed}
        ref={setMoverRef}
        {...rest}
      >
        <Static $inline={widthMotion} ref={contentsRef}>
          {children}
        </Static>
      </Mover>
    );
  }
);

FnrMovingDiv.propTypes = {
  heightMotion: bool,
  widthMotion: bool,
  children: node,
  spring: bool,
  clip: bool,
  muted: bool,
  transitionOverride: object,
  shrinkOnly: bool,
};

export default FnrMovingDiv;
